import { getCell } from "@/grid/data/assemble-grid";
import type { DepartmentId, GridViewModel, PeriodId } from "@/grid/core/types";

export type ExportPacing = "ahead" | "on-track" | "behind";

export interface ExportMetricForLLM {
  actual: number;
  target: number;
  pacing: ExportPacing;
  percentOfTarget: number;
}

export interface ExportTeamMetricForLLM {
  wtd: ExportMetricForLLM;
  lastWeek: { actual: number; target: number };
  cumulative: ExportMetricForLLM;
}

export interface ExportTeamForLLM {
  name: string;
  metrics: Record<string, ExportTeamMetricForLLM>;
}

export interface ExportTeamDataPayload {
  teams: ExportTeamForLLM[];
  fetchedAt: string;
}

function getPacing(actual: number, target: number): ExportPacing {
  if (target === 0) return "on-track";
  const ratio = actual / target;
  if (ratio >= 1) return "ahead";
  if (ratio >= 0.8) return "on-track";
  return "behind";
}

function metricForLLM(actual: number, target: number): ExportMetricForLLM {
  return {
    actual,
    target,
    pacing: getPacing(actual, target),
    percentOfTarget: target > 0 ? Math.round((actual / target) * 100) : 0,
  };
}

function cumulativePeriodId(departmentId: DepartmentId): PeriodId {
  return departmentId === "sales" ? "qtd" : "mtd";
}

export function buildExportTeamData(viewModel: GridViewModel): ExportTeamDataPayload {
  const cumulativePeriod = cumulativePeriodId(viewModel.department.id);

  const teams: ExportTeamForLLM[] = viewModel.teams.map((team) => {
    const metrics: Record<string, ExportTeamMetricForLLM> = {};

    for (const metric of viewModel.department.metrics) {
      const wtdCell = getCell(team, metric.id, "wtd");
      const lastWeekCell = getCell(team, metric.id, "lastWeek");
      const cumulativeCell = getCell(team, metric.id, cumulativePeriod);

      const wtdActual = wtdCell?.actual ?? 0;
      const wtdTarget = wtdCell?.pacingTarget ?? wtdCell?.target ?? 0;
      const cumulativeActual = cumulativeCell?.actual ?? 0;
      const cumulativeTarget = cumulativeCell?.pacingTarget ?? cumulativeCell?.target ?? 0;

      metrics[metric.id] = {
        wtd: metricForLLM(wtdActual, wtdTarget),
        lastWeek: {
          actual: lastWeekCell?.actual ?? 0,
          target: lastWeekCell?.target ?? 0,
        },
        cumulative: metricForLLM(cumulativeActual, cumulativeTarget),
      };
    }

    return { name: team.name, metrics };
  });

  return {
    teams,
    fetchedAt: viewModel.fetchedAt,
  };
}
